import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Plus, Trash2, Loader2, Save, X, Megaphone } from 'lucide-react';
import { getToken, clearToken, me } from '../lib/api';
import { formatAnnouncementExpiry, getAnnouncementExpiryInput, isAnnouncementCurrent } from '../lib/announcementExpiry';
import { useToast } from '../hooks/use-toast';
import { iceLogo } from '../mock';
import { ChampionsBannerControl } from '../components/ChampionsBannerControl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = [
  { value: 'practice_cancellation', label: 'Practice Cancellation' },
  { value: 'weather_cancellation', label: 'Weather Cancellation' },
  { value: 'game_cancellation', label: 'Game Cancellation' },
  { value: 'venue_change', label: 'Venue Change' },
  { value: 'upcoming_event', label: 'Upcoming Event' }
];

const emptyForm = { type: 'practice_cancellation', title: '', body: '', imageUrl: '', imageUrlsText: '', imageLayout: 'grid', expiresAt: '', kickoffTime: '', active: true };

export default function AdminAnnouncements() {
  const { toast } = useToast();
  const [authOk, setAuthOk] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const authHdr = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  useEffect(() => {
    if (!getToken()) { setAuthOk(false); return; }
    me().then(() => setAuthOk(true)).catch(() => { clearToken(); setAuthOk(false); });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/announcements/all`, authHdr());
      setList(data);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { if (authOk) load(); }, [authOk, load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        imageUrls: form.imageUrlsText.split('\n').map((url) => url.trim()).filter(Boolean),
      };
      delete payload.imageUrlsText;
      if (editingId) {
        await axios.put(`${API}/announcements/${editingId}`, payload, authHdr());
        toast({ title: 'Updated' });
      } else {
        await axios.post(`${API}/announcements`, payload, authHdr());
        toast({ title: 'Created' });
      }
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const editRow = (a) => {
    setEditingId(a.id);
    setForm({ type: a.type, title: a.title, body: a.body, imageUrl: a.imageUrl || '', imageUrlsText: (a.imageUrls || []).join('\n'), imageLayout: a.imageLayout || 'grid', expiresAt: getAnnouncementExpiryInput(a), kickoffTime: a.kickoffTime || '', active: a.active });
  };
  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); };

  const toggle = async (a) => {
    await axios.put(`${API}/announcements/${a.id}`, { ...a, active: !a.active }, authHdr());
    await load();
  };
  const remove = async (announcement) => {
    if (!window.confirm(`Remove “${announcement.title}” now? This cannot be undone.`)) return;
    setRemovingId(announcement.id);
    try {
      await axios.delete(`${API}/announcements/${announcement.id}`, authHdr());
      toast({ title: 'Announcement removed' });
      await load();
    } catch (e) {
      toast({ title: 'Remove failed', description: e.response?.data?.detail || e.message });
    } finally { setRemovingId(null); }
  };

  if (authOk === null) return <div className="min-h-screen bg-[#0a1f44] flex items-center justify-center text-white">Loading…</div>;
  if (authOk === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#f5f6f8]">
      <header className="bg-[#0a1f44] text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={iceLogo} alt="ICE" className="h-10 w-auto" />
            <div>
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Admin Console</div>
              <div className="font-display text-2xl leading-none mt-1">ANNOUNCEMENTS</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/scholars" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Scholars</Link>
            <Link to="/admin/orders" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Orders</Link>
            <Link to="/admin/inventory" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Inventory</Link>
            <Link to="/admin/products" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Prices</Link>
            <Link to="/admin/season" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Season</Link>
            <Link to="/admin/registrations" data-testid="admin-nav-registrations" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Registrations</Link>
            <Link to="/admin/fundraisers" data-testid="admin-nav-fundraisers" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">RSVPs</Link>
            <Link to="/admin/weekly-text" data-testid="admin-nav-weekly-text" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Weekly Text</Link>
            <Link to="/admin/power-rankings" data-testid="admin-nav-power-rankings" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Rankings</Link>
            <Link to="/admin/account" data-testid="admin-nav-account" className="text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">Change Password</Link>
            <button onClick={() => { clearToken(); window.location.href = '/admin/login'; }} className="inline-flex items-center gap-1 text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20">
              <LogOut className="w-3 h-3" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <ChampionsBannerControl />
        {/* Create / Edit form */}
        <form onSubmit={submit} className="bg-white border border-[#0a1f44]/10 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#c8102e] flex items-center justify-center text-white"><Megaphone className="w-5 h-5" /></div>
            <div className="font-display text-[#0a1f44] text-xl">{editingId ? 'EDIT ANNOUNCEMENT' : 'NEW ANNOUNCEMENT'}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="ann-form-type" className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]">
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Expires (Eastern Time)</label>
              <input type="datetime-local" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} required={!editingId} data-testid="ann-form-expires-at" className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]" />
              <div className="text-[10px] text-[#0a1f44]/50 mt-1" data-testid="ann-form-expiry-help">Required for new posts. Removes it automatically at the selected Eastern time.</div>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="ann-form-active" className="w-4 h-4 accent-[#c8102e]" />
                Show to visitors
              </label>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Title *</label>
            <input required type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ann-form-title" className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]" placeholder="e.g. Tuesday practice cancelled — thunderstorm warning" />
          </div>
          <div>
            <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Details</label>
            <textarea rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="ann-form-body" className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]" placeholder="Optional longer message shown below the title." />
          </div>
          <div>
            <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Game Day Image URL</label>
            <input type="url" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} data-testid="ann-form-image-url" className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]" placeholder="https://…/game-day-flyer.png" />
          </div>
          <div>
            <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Side-by-Side Gallery URLs</label>
            <textarea rows={4} value={form.imageUrlsText} onChange={(e) => setForm({ ...form, imageUrlsText: e.target.value })} data-testid="ann-form-image-urls" className="w-full border border-[#0a1f44]/20 px-3 py-2 text-base focus:outline-none focus:border-[#c8102e]" placeholder={'One https:// image URL per line\nUp to 8 images'} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Gallery Layout</label>
              <select value={form.imageLayout} onChange={(e) => setForm({ ...form, imageLayout: e.target.value })} data-testid="ann-form-image-layout" className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]">
                <option value="grid">Equal grid</option>
                <option value="featured_with_three">Featured flyer + three side-by-side</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Kickoff Time (Eastern, optional)</label>
              <input type="time" value={form.kickoffTime} onChange={(e) => setForm({ ...form, kickoffTime: e.target.value })} data-testid="ann-form-kickoff-time" className="w-full border border-[#0a1f44]/20 px-3 py-2 focus:outline-none focus:border-[#c8102e]" />
              <div className="text-[10px] text-[#0a1f44]/50 mt-1">Shows a live kickoff countdown on the event date.</div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            {editingId && <button type="button" onClick={cancelEdit} className="text-[#0a1f44]/60 hover:text-[#c8102e] font-heading uppercase tracking-widest text-[10px] px-3 py-2"><X className="w-4 h-4 inline mr-1" /> Cancel</button>}
            <button type="submit" disabled={busy} data-testid="ann-form-submit" className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] disabled:opacity-50 text-white font-heading uppercase tracking-widest text-xs px-6 py-3 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />)}
              {editingId ? 'Save Changes' : 'Publish Announcement'}
            </button>
          </div>
        </form>

        {/* List */}
        {loading ? (
          <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0a1f44]/50" /></div>
        ) : (
          <div className="bg-white border border-[#0a1f44]/10 overflow-x-auto" data-testid="announcements-table-scroll-region">
            <table className="mobile-stack-table w-full min-w-[760px] text-sm" data-testid="announcements-table">
              <thead className="bg-[#0a1f44] text-white">
                <tr>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Type</th>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Title</th>
                  <th className="px-3 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Expires</th>
                  <th className="px-3 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Active</th>
                  <th className="px-3 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-[#0a1f44]/50 text-sm">No announcements yet — create one above.</td></tr>
                ) : list.map((a) => {
                  const expired = !isAnnouncementCurrent(a);
                  return <tr key={a.id} data-testid={`ann-row-${a.id}`} className="border-t border-[#0a1f44]/5">
                    <td data-label="Type" className="px-3 py-3 text-xs font-heading uppercase tracking-widest text-[#c8102e]">{TYPES.find((t) => t.value === a.type)?.label || a.type}</td>
                    <td data-label="Title" className="px-3 py-3">
                      <div className="font-heading text-[#0a1f44]">{a.title}</div>
                      {(a.imageUrls?.length ? a.imageUrls : (a.imageUrl ? [a.imageUrl] : [])).length > 0 && <div className="mt-2 flex gap-1" data-testid={`ann-admin-images-${a.id}`}>{(a.imageUrls?.length ? a.imageUrls : [a.imageUrl]).slice(0, 4).map((url, index) => <img key={url} src={url} alt={`${a.title} preview ${index + 1}`} data-testid={`ann-admin-image-${a.id}-${index + 1}`} className="h-14 w-10 bg-[#0a1f44] object-contain" loading="lazy" decoding="async" />)}</div>}
                      <div className="mt-1 text-[9px] font-heading uppercase tracking-wider text-[#0a1f44]/45" data-testid={`ann-admin-layout-${a.id}`}>{a.imageLayout === 'featured_with_three' ? 'Featured flyer + three' : 'Equal grid'}</div>
                      {a.body && <div className="text-xs text-[#0a1f44]/60 line-clamp-2">{a.body}</div>}
                    </td>
                    <td data-label="Expires" className="px-3 py-3 text-xs text-[#0a1f44]/65" data-testid={`ann-expires-at-${a.id}`}>{formatAnnouncementExpiry(a)}</td>
                    <td data-label="Active" className="px-3 py-3 text-center">
                      <button onClick={() => toggle(a)} data-testid={`ann-toggle-${a.id}`} className={`font-heading uppercase tracking-widest text-[9px] px-3 py-1 transition-colors ${expired ? 'bg-[#0a1f44]/10 text-[#0a1f44]/60 hover:bg-[#0a1f44]/20' : a.active ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' : 'bg-[#0a1f44]/10 text-[#0a1f44]/60 hover:bg-[#0a1f44]/20'}`}>
                        {expired ? 'Expired' : a.active ? 'On' : 'Off'}
                      </button>
                    </td>
                    <td data-label="Actions" className="px-3 py-3 text-center">
                      <div className="inline-flex gap-1">
                        <button onClick={() => editRow(a)} data-testid={`ann-edit-${a.id}`} className="p-2 text-[#0a1f44]/60 hover:text-[#c8102e]"><Save className="w-4 h-4" /></button>
                        <button type="button" onClick={() => remove(a)} disabled={removingId === a.id} data-testid={`ann-delete-${a.id}`} className="inline-flex items-center gap-1.5 border border-[#c8102e] px-3 py-2 text-[#c8102e] hover:text-white hover:bg-[#c8102e] disabled:opacity-50 font-heading uppercase tracking-widest text-[9px] transition-colors">{removingId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Remove Now</button>
                      </div>
                    </td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
